import { useCallback, useMemo } from "react"
import {
  Typography,
  Stack,
  Card,
  Box,
  Divider,
  useTheme,
  Button,
} from "@mui/material"
import _ from "lodash"
import EmailIcon from "@mui/icons-material/Email"
import LocalPhoneIcon from "@mui/icons-material/LocalPhone"
import { deepmerge } from "@mui/utils"

import { Link } from "react-router-dom"

import config from "config"

import { useTranslation } from "providers/i18n"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { ListPrivilegeItemCard } from "components/domains/privilege"
import { Image, ImageModal } from "components/Image"
import { Carousel, CarouselItem } from "components/Carousel"
import { WebsiteIcon } from "components/icons"
import { FeatureLabel } from "components/domains/features/FeatureLabel"
import { HighlightFeatureCard } from "components/domains/events"

const DEFAULT_GALLERY_CAROUSEL_OPTIONS = {
  fixedWidth: "312px",
  fixedHeight: "235px",
  perPage: 1,
  gap: 12,
  padding: { left: 20, right: 20 },
}

const DEFAULT_PROMOTION_CAROUSEL_OPTIONS = {
  perPage: 1,
  fixedWidth: 355,
  fixedHeight: 144,
  gap: 12,
  padding: { left: 20, right: 20 },
}

export const FeatureDetail = ({ feature, loading, ...props }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const {
    root,
    listContainer,
    featureLabel,
    operationTimeLabel,
    section,
    galleryImage,
    divider,
    contactButton,
    sectionTitle,
    descriptionText,
    promotionCarouselItem,
    contactWrapper,
  } = theme.components[`AppFeatureDetail`]?.styleOverrides || {}
  const { galleryCarouselOptions = DEFAULT_GALLERY_CAROUSEL_OPTIONS } =
    theme.components[`AppFeatureDetail`]?.defaultProps || {}

  const {
    websiteLink,
    phone,
    email,
    promotions,
    description,
    gallery,
    privileges,
    level,
    category,
    hours,
  } = props

  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const featurePromotions = useMemo(
    () =>
      promotions?.filter(({ properties }) =>
        _.includes(["store", "shop"], properties.category)
      ) || [],
    [promotions]
  )

  const handleClickContact = useCallback(
    (contact) => {
      if (isKioskMode) return
      window.open(contact)
    },
    [isKioskMode]
  )

  if (loading) return null

  return (
    <>
      <Card className="FeatureDetail-root" sx={root}>
        <Box sx={listContainer}>
          <FeatureLabel
            label={t("Location")}
            value={t(level)}
            sx={featureLabel}
          />
          <FeatureLabel
            label={t("Category")}
            value={t(category)}
            sx={featureLabel}
          />
          <FeatureLabel
            label={t("Operation Time")}
            value={hours || "-"}
            sx={operationTimeLabel}
          />
        </Box>
        {gallery && (
          <Box className="FeatureDetail-section" sx={section}>
            <Carousel
              options={deepmerge(
                DEFAULT_GALLERY_CAROUSEL_OPTIONS,
                galleryCarouselOptions
              )}
            >
              {gallery?.map((image, idx) => (
                <CarouselItem key={`${image.name}-${idx}`}>
                  <Image
                    onClick={() => handleImage(image.url)}
                    src={image.url}
                    alt={`${image.name}-${idx}`}
                    sx={galleryImage}
                  />
                </CarouselItem>
              ))}
            </Carousel>
          </Box>
        )}
        {(websiteLink || phone || email) && (
          <>
            <Box className="FeatureDetail-section" sx={contactWrapper}>
              {websiteLink && (
                <Button
                  variant="text"
                  onClick={() => handleClickContact(websiteLink)}
                  startIcon={<WebsiteIcon />}
                  sx={contactButton}
                >
                  {websiteLink}
                </Button>
              )}
              {phone && (
                <Button
                  variant="text"
                  onClick={() => handleClickContact(`tel:${phone}`)}
                  startIcon={<LocalPhoneIcon />}
                  sx={contactButton}
                >
                  {phone}
                </Button>
              )}
              {email && (
                <Button
                  variant="text"
                  onClick={() => handleClickContact(`mailto:${email}`)}
                  startIcon={<EmailIcon />}
                  sx={contactButton}
                >
                  {email}
                </Button>
              )}
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {description && (
          <>
            <Box className="FeatureDetail-section" sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Description")}
              </Typography>
              <Typography variant="body2" sx={descriptionText}>
                {t(description)}
              </Typography>
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {featurePromotions?.length > 0 && (
          <>
            <Box className="FeatureDetail-section" sx={section}>
              <Carousel options={DEFAULT_PROMOTION_CAROUSEL_OPTIONS}>
                {promotions?.length > 0 &&
                  promotions.map((featurePromotion) => (
                    <CarouselItem
                      key={featurePromotion.id}
                      className={featurePromotion.properties?.category}
                      sx={promotionCarouselItem}
                    >
                      <Link
                        to={`/promotions/${featurePromotion.id}?redirectTo=-1`}
                        key={featurePromotion.id}
                      >
                        <HighlightFeatureCard feature={featurePromotion} />
                      </Link>
                    </CarouselItem>
                  ))}
              </Carousel>
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {privileges?.length > 0 && (
          <>
            <Box className="FeatureDetail-section" sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Tourist Privileges")}
              </Typography>
              <Stack spacing={2}>
                {privileges.map((privilege) => (
                  <ListPrivilegeItemCard key={privilege.id} {...privilege} />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Card>
      <ImageModal
        open={openModal}
        imageSrc={activeImage}
        onClose={handleClose}
      />
    </>
  )
}
